import React from 'react'
import { motion } from 'framer-motion'
import { 
  ArrowUp, 
  ArrowDown, 
  ArrowLeft, 
  ArrowRight,
  Square,
  RotateCcw
} from 'lucide-react'

const ButtonControl = ({
  isConnected,
  sendCarCommand,
  currentDirection,
  isMoving
}) => {
  const handleButtonPress = (command) => {
    if (!isConnected) return
    sendCarCommand(command)
  }

  const handleButtonRelease = () => {
    if (!isConnected) return
    sendCarCommand('car_stop')
  }

  const isActive = (direction) => {
    return currentDirection === direction
  }

  const buttonVariants = {
    idle: { scale: 1 },
    pressed: { scale: 0.95 },
    active: { 
      scale: 1.05,
      boxShadow: '0 0 20px rgba(59, 130, 246, 0.5)'
    }
  }

  return (
    <div className="flex flex-col items-center space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Button Control</h3>
        <p className="text-sm text-gray-400">
          {isConnected ? 'Click buttons to control the car' : 'Connect to a device first'}
        </p>
      </div>

      {/* Control Grid */}
      <div className="grid grid-cols-3 gap-4">
        {/* Top row - Forward and diagonal forward */}
        <div className="col-start-2">
          <motion.button
            variants={buttonVariants}
            animate={isActive('FORWARD') ? 'active' : 'idle'}
            whileHover={isConnected ? { scale: 1.05 } : {}}
            whileTap={isConnected ? 'pressed' : {}}
            onMouseDown={() => handleButtonPress('car_forward')}
            onMouseUp={handleButtonRelease}
            onMouseLeave={handleButtonRelease}
            onTouchStart={() => handleButtonPress('car_forward')}
            onTouchEnd={handleButtonRelease}
            disabled={!isConnected}
            className="control-button bg-gradient-to-br from-success-600 to-success-700"
          >
            <ArrowUp className="w-6 h-6" />
          </motion.button>
        </div>

        {/* Middle row - Left, Stop, Right */}
        <div className="col-start-1">
          <motion.button
            variants={buttonVariants}
            animate={isActive('LEFT') ? 'active' : 'idle'}
            whileHover={isConnected ? { scale: 1.05 } : {}}
            whileTap={isConnected ? 'pressed' : {}}
            onMouseDown={() => handleButtonPress('car_left')}
            onMouseUp={handleButtonRelease}
            onMouseLeave={handleButtonRelease}
            onTouchStart={() => handleButtonPress('car_left')}
            onTouchEnd={handleButtonRelease}
            disabled={!isConnected}
            className="control-button bg-gradient-to-br from-warning-600 to-warning-700"
          >
            <ArrowLeft className="w-6 h-6" />
          </motion.button>
        </div>

        <div className="col-start-2">
          <motion.button
            variants={buttonVariants}
            animate={isActive('STOP') ? 'active' : 'idle'}
            whileHover={isConnected ? { scale: 1.05 } : {}}
            whileTap={isConnected ? 'pressed' : {}}
            onClick={() => handleButtonPress('car_stop')}
            disabled={!isConnected}
            className="control-button bg-gradient-to-br from-danger-600 to-danger-700"
          >
            <Square className="w-6 h-6" />
          </motion.button>
        </div>

        <div className="col-start-3">
          <motion.button
            variants={buttonVariants}
            animate={isActive('RIGHT') ? 'active' : 'idle'}
            whileHover={isConnected ? { scale: 1.05 } : {}}
            whileTap={isConnected ? 'pressed' : {}}
            onMouseDown={() => handleButtonPress('car_right')}
            onMouseUp={handleButtonRelease}
            onMouseLeave={handleButtonRelease}
            onTouchStart={() => handleButtonPress('car_right')}
            onTouchEnd={handleButtonRelease}
            disabled={!isConnected}
            className="control-button bg-gradient-to-br from-warning-600 to-warning-700"
          >
            <ArrowRight className="w-6 h-6" />
          </motion.button>
        </div>

        {/* Bottom row - Backward and diagonal backward */}
        <div className="col-start-2">
          <motion.button
            variants={buttonVariants}
            animate={isActive('BACKWARD') ? 'active' : 'idle'}
            whileHover={isConnected ? { scale: 1.05 } : {}}
            whileTap={isConnected ? 'pressed' : {}}
            onMouseDown={() => handleButtonPress('car_backward')}
            onMouseUp={handleButtonRelease}
            onMouseLeave={handleButtonRelease}
            onTouchStart={() => handleButtonPress('car_backward')}
            onTouchEnd={handleButtonRelease}
            disabled={!isConnected}
            className="control-button bg-gradient-to-br from-danger-600 to-danger-700"
          >
            <ArrowDown className="w-6 h-6" />
          </motion.button>
        </div>
      </div>

      {/* Diagonal Controls */}
      <div className="grid grid-cols-2 gap-4">
        <motion.button
          variants={buttonVariants}
          animate={isActive('LEFT-FORWARD') ? 'active' : 'idle'}
          whileHover={isConnected ? { scale: 1.05 } : {}}
          whileTap={isConnected ? 'pressed' : {}}
          onMouseDown={() => handleButtonPress('car_left_forward')}
          onMouseUp={handleButtonRelease}
          onMouseLeave={handleButtonRelease}
          onTouchStart={() => handleButtonPress('car_left_forward')}
          onTouchEnd={handleButtonRelease}
          disabled={!isConnected}
          className="control-button bg-gradient-to-br from-primary-600 to-primary-700"
        >
          <div className="flex items-center space-x-1">
            <ArrowUp className="w-4 h-4" />
            <ArrowLeft className="w-4 h-4" />
          </div>
        </motion.button>

        <motion.button
          variants={buttonVariants}
          animate={isActive('RIGHT-FORWARD') ? 'active' : 'idle'}
          whileHover={isConnected ? { scale: 1.05 } : {}}
          whileTap={isConnected ? 'pressed' : {}}
          onMouseDown={() => handleButtonPress('car_right_forward')}
          onMouseUp={handleButtonRelease}
          onMouseLeave={handleButtonRelease}
          onTouchStart={() => handleButtonPress('car_right_forward')}
          onTouchEnd={handleButtonRelease}
          disabled={!isConnected}
          className="control-button bg-gradient-to-br from-primary-600 to-primary-700"
        >
          <div className="flex items-center space-x-1">
            <ArrowUp className="w-4 h-4" />
            <ArrowRight className="w-4 h-4" />
          </div>
        </motion.button>

        <motion.button
          variants={buttonVariants}
          animate={isActive('LEFT-BACKWARD') ? 'active' : 'idle'}
          whileHover={isConnected ? { scale: 1.05 } : {}}
          whileTap={isConnected ? 'pressed' : {}}
          onMouseDown={() => handleButtonPress('car_left_backward')}
          onMouseUp={handleButtonRelease}
          onMouseLeave={handleButtonRelease}
          onTouchStart={() => handleButtonPress('car_left_backward')}
          onTouchEnd={handleButtonRelease}
          disabled={!isConnected}
          className="control-button bg-gradient-to-br from-secondary-600 to-secondary-700"
        >
          <div className="flex items-center space-x-1">
            <ArrowDown className="w-4 h-4" />
            <ArrowLeft className="w-4 h-4" />
          </div>
        </motion.button>

        <motion.button
          variants={buttonVariants}
          animate={isActive('RIGHT-BACKWARD') ? 'active' : 'idle'}
          whileHover={isConnected ? { scale: 1.05 } : {}}
          whileTap={isConnected ? 'pressed' : {}}
          onMouseDown={() => handleButtonPress('car_right_backward')}
          onMouseUp={handleButtonRelease}
          onMouseLeave={handleButtonRelease}
          onTouchStart={() => handleButtonPress('car_right_backward')}
          onTouchEnd={handleButtonRelease}
          disabled={!isConnected}
          className="control-button bg-gradient-to-br from-secondary-600 to-secondary-700"
        >
          <div className="flex items-center space-x-1">
            <ArrowDown className="w-4 h-4" />
            <ArrowRight className="w-4 h-4" />
          </div>
        </motion.button>
      </div>

      {/* Status */}
      <div className="text-center">
        <div className="text-sm text-gray-400 mb-1">Current Direction</div>
        <div className="text-lg font-semibold text-primary-400">
          {currentDirection || 'STOP'}
        </div>
        {isMoving && (
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="text-xs text-success-400 mt-1"
          >
            Moving
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default ButtonControl 